import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./Header.scss";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import LoginForm from "../SignIn/LoginForm";
import RegisterForm from "../Registration/RegisterForm";
import ForgotPasswordForm from "../SignIn/ForgotPasswordForm";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  LOGIN_URL,
  REGISTER_URL,
  FORGOT_PASSWORD_URL,
  IS_AUTHENTICATED_URL,
  RESEND_VERIFICATION_URL,
} from "../../features/UrlHelper";
import { handleApiError } from "../../features/errorHandler";
import {
  Check,
  Mail,
  User,
  Shield,
  ShieldCheck,
  LogOut,
  LogIn,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Package,
  GraduationCap,
  LayoutDashboard,
  Users,
  UserCheck,
  Coins,
  ShoppingBag,
  CalendarDays,
  CalendarX2,
  CreditCard,
  Receipt,
  School,
} from "lucide-react";
import { pushGTMEvent } from "../../analytics/gtm";

const ADMIN_NAV_LINKS = [
  { key: "orders", label: "Správa objednávok", icon: Package },
  { key: "cancellations", label: "Zrušené lekcie", icon: CalendarX2 },
  { key: "users", label: "Správa užívateľov", icon: Users },
  { key: "schools", label: "Základné školy", icon: School },
  { key: "osoby", label: "Osoby", icon: UserCheck },
  { key: "kredity", label: "Kredity", icon: Coins },
  { key: "katalog", label: "Katalóg krúžkov", icon: ShoppingBag },
  { key: "lektor", label: "Rozhranie lektora", icon: GraduationCap, directPath: "/lektor" },
];

function Header({ logo, className = "" }) {
  const {
    isLoggedIn,
    isAdmin,
    isLektor,
    userEmail,
    userName,
    userCredits,
    login,
    logout,
    loginModalOpen,
    registerModalOpen,
    forgotModalOpen,
    setLoginModalOpen,
    setRegisterModalOpen,
    setForgotModalOpen,
    openLoginModal,
    openRegisterModal,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [errorMessageRegistration, setErrorMessageRegistration] = useState("");
  const [errorMessageLogin, setErrorMessageLogin] = useState("");
  const [errorMessageForgot, setErrorMessageForgot] = useState("");
  const [registrationinng, setRegistrationing] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [forgotting, setForgetting] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendStatus, setResendStatus] = useState("idle"); // idle | loading | success | error
  const [resendMessage, setResendMessage] = useState("");

  const isHome = location.pathname === "/" || location.pathname === "/o-nas";
  const loginCaptchaRef = useRef();
  const registerCaptchaRef = useRef();
  const forgotCaptchaRef = useRef();

  // Close user dropdown on click outside
  useEffect(() => {
    if (!userDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userDropdownOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // widgety pre registraciu, prihlasenie a zabudnute heslo
  const [captchaTokenLogin, setCaptchaTokenLogin] = useState(null);
  const [captchaTokenRegister, setCaptchaTokenRegister] = useState(null);
  const [captchaTokenForgot, setCaptchaTokenForgot] = useState(null);

  const links = [
    { label: "Ponuka", to: "/#ponuka", hashId: "ponuka" },
    { label: "O nás", to: "/o-nas", hashId: "o-nas" },
    { label: "Rozvrh", to: "/#rozvrh", hashId: "rozvrh" },
    { label: "Kontakt", to: "/#kontakt", hashId: "kontakt" },
  ];

  const handleNavClick = (e, link) => {
    setIsOpen(false);
    if (link.hashId) {
      if (
        location.pathname === "/" ||
        location.pathname === "/o-nas" ||
        location.pathname === "/ponuka" ||
        location.pathname === "/katalog-clenstiev" ||
        location.pathname === "/clenstva"
      ) {
        const el = document.getElementById(link.hashId);
        if (el) {
          e.preventDefault();
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          window.history.pushState(null, "", `#${link.hashId}`);
        }
      }
    }
  };

  const isLinkActive = (link) => {
    if (link.hashId === "ponuka") {
      return (
        location.hash === "#ponuka" ||
        location.hash === "#kruzky" ||
        location.pathname === "/ponuka" ||
        location.pathname === "/katalog-clenstiev" ||
        location.pathname === "/clenstva"
      );
    }
    if (link.hashId === "o-nas") {
      return location.pathname === "/o-nas" || location.hash === "#o-nas";
    }
    if (link.hashId === "rozvrh") {
      return location.pathname === "/rozvrh" || location.hash === "#rozvrh";
    }
    if (link.hashId === "kontakt") {
      return location.pathname === "/kontakt" || location.hash === "#kontakt";
    }
    return location.pathname === link.to;
  };

  useEffect(() => {
    setErrorMessageForgot("");
    setErrorMessageLogin("");
    setErrorMessageRegistration("");
    setLoggingIn(false);
    setRegistrationing(false);
    setForgetting(false);
    if (!registerModalOpen) setRegisterSuccess(false);
    if (!forgotModalOpen) setForgotSuccess(false);
  }, [registerModalOpen, loginModalOpen]);

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) setIsOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function handleLogin(params) {
    if (!captchaTokenLogin) {
      console.error(
        "Login error:",
        new Error("Captcha token není k dispozici."),
      );
      setLoggingIn(false);
      return;
    }
    await axios
      .post(LOGIN_URL, {
        email: params?.email ?? "",
        password: params?.password ?? "",
        cfTurnstileToken: captchaTokenLogin,
      })
      .then((res) => {
        window.localStorage.setItem("authToken", res.data.token);
        setErrorMessageLogin("");
        login(
          res.data.admin === true,
          res.data.email ?? null,
          res.data.id ?? null,
        );
        setLoginModalOpen(false);
        setLoggingIn(false);
        setCaptchaTokenLogin(null);
      })
      .catch((err) => {
        setCaptchaTokenLogin(null);
        loginCaptchaRef.current?.reset();
        if (
          err.response &&
          err.response.status === 400 &&
          err.response.data.type !== undefined &&
          err.response.data.type === "verification"
        ) {
          setResendEmail(params?.email ?? "");
          setLoginModalOpen(false);
          setResendOpen(true);
          setResendStatus("idle");
          setResendMessage("");
          setLoggingIn(false);
          return;
        }
        setLoggingIn(false);
        const msg = handleApiError(err, "Chyba pri prihlásení. Skontrolujte údaje.");
        setErrorMessageLogin(msg);
      });
  }

  async function handleIsLoggedIn() {
    const token = window.localStorage.getItem("authToken");
    if (!token) {
      return;
    }

    await axios
      .get(IS_AUTHENTICATED_URL, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        console.log("Is authenticated response:", res.data);
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          window.localStorage.removeItem("authToken");
          logout();
        } else {
          handleApiError(err, "Is authenticated error");
        }
      });
  }

  async function handleRegister(params) {
    if (!captchaTokenRegister) {
      console.error(
        "Registration error:",
        new Error("Captcha token není k dispozici."),
      );
      setRegistrationing(false);
      return;
    }
    await axios
      .post(REGISTER_URL, {
        email: params?.email ?? "",
        password: params?.password ?? "",
        passwordConfirmation: params?.confirmPassword ?? "",
        firstName: params?.firstName ?? "",
        lastName: params?.lastName ?? "",
        street: params?.street ?? "",
        zip: params?.zip ?? "",
        city: params?.city ?? "",
        agree: params?.agree ?? false,
        photoConsent: params?.photoConsent ?? false,
        cfTurnstileToken: captchaTokenRegister,
      })
      .then((res) => {
        setErrorMessageRegistration("");
        setRegisterSuccess(true);
        setRegistrationing(false);
        setCaptchaTokenRegister(null);
        pushGTMEvent("sign_up", { method: "email" });
      })
      .catch((err) => {
        setCaptchaTokenRegister(null);
        registerCaptchaRef.current?.reset();
        setRegistrationing(false);
        const msg = handleApiError(err, "Chyba pri registrácii. Skontrolujte údaje.");
        setErrorMessageRegistration(msg);
      });
  }

  async function handleForgotPassword(params) {
    if (!/\S+@\S+\.\S+/.test(params?.email)) {
      console.error(
        "Forgot password error:",
        new Error("Neplatný formát emailu."),
      );
      return;
    }
    if (!captchaTokenForgot) {
      console.error(
        "Forgot password error:",
        new Error("Captcha token není k dispozici."),
      );
      setForgetting(false);
      return;
    }
    await axios
      .post(FORGOT_PASSWORD_URL, {
        email: params?.email ?? "",
        cfTurnstileToken: captchaTokenForgot,
      })
      .then((res) => {
        setForgetting(false);
        setErrorMessageForgot("");
        setForgotSuccess(true);
        setCaptchaTokenForgot(null);
      })
      .catch((err) => {
        setCaptchaTokenForgot(null);
        forgotCaptchaRef.current?.reset();
        setForgetting(false);
        const msg = handleApiError(err, "Chyba pri odosielaní nového hesla. Skúste to znova.");
        setErrorMessageForgot(msg);
      });
  }

  return (
    <>
      <header
        className={`header ${isHome ? "header--home" : ""} ${scrolled ? "header--scrolled" : "header--top"
          } ${className}`}
      >
        <div className="header__container">
          <Link to="/" className="header__logo">
            <img
              src="/pictures/triferis/triferis-logo-horizontal.svg"
              alt="Penzión Triferis"
              className="header__logo-img"
            />
          </Link>

          <nav className={`header__nav ${isOpen ? "header__nav--open" : ""}`}>
            {/* Mobile-only User Profile Card */}
            {isLoggedIn && (
              <div className="header__mobile-user-card">
                <div className="header__mobile-user-header">
                  <div className="header__mobile-avatar">
                    {userName ? (
                      userName.charAt(0).toUpperCase()
                    ) : userEmail ? (
                      userEmail.charAt(0).toUpperCase()
                    ) : (
                      <User size={18} />
                    )}
                  </div>
                  <div className="header__mobile-user-meta">
                    <strong className="header__mobile-user-name">
                      {userName || "Prihlásený používateľ"}
                    </strong>
                    <span className="header__mobile-user-email">{userEmail}</span>
                  </div>
                </div>

                <div className="header__mobile-credits-bar">
                  <div className="header__mobile-credits-label">
                    <Sparkles size={14} />
                    <span>Dostupné kredity:</span>
                  </div>
                  <strong className="header__mobile-credits-value">
                    {userCredits} {userCredits === 1 ? "kredit" : userCredits >= 2 && userCredits <= 4 ? "kredity" : "kreditov"}
                  </strong>
                </div>

                <div className="header__mobile-user-links">
                  <Link
                    to="/profil"
                    className="header__mobile-menu-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <User size={16} />
                    <span>Môj profil</span>
                  </Link>
                  <Link
                    to="/moje-objednavky"
                    className="header__mobile-menu-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <Package size={16} />
                    <span>Moje objednávky</span>
                  </Link>
                  <Link
                    to="/moj-rozvrh"
                    className="header__mobile-menu-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <CalendarDays size={16} />
                    <span>Rozvrh mojich detí</span>
                  </Link>
                  {(isLektor || isAdmin) && (
                    <Link
                      to="/lektor"
                      className="header__mobile-menu-item"
                      onClick={() => setIsOpen(false)}
                    >
                      <GraduationCap size={16} />
                      <span>Rozhranie lektora</span>
                    </Link>
                  )}
                </div>

                {isAdmin && (
                  <div className="header__mobile-admin-section">
                    <span className="header__mobile-section-title">
                      <Shield size={13} /> Administrácia
                    </span>
                    <div className="header__mobile-admin-links">
                      {ADMIN_NAV_LINKS.map((item) => {
                        const Icon = item.icon;
                        const targetTo = item.directPath || `/admin?tab=${item.key}`;
                        return (
                          <Link
                            key={item.key}
                            to={targetTo}
                            className="header__mobile-admin-item"
                            onClick={() => setIsOpen(false)}
                          >
                            <Icon size={15} />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="header__dropdown-logout-btn header__mobile-logout-btn"
                  onClick={() => {
                    setIsOpen(false);
                    logout();
                    navigate("/");
                  }}
                >
                  <LogOut size={16} />
                  <span>Odhlásiť sa</span>
                </button>
              </div>
            )}

            <div className="header__links-group">
              {links.map((link, i) => (
                <Link
                  key={i}
                  to={link.to}
                  className={`header__link ${isLinkActive(link) ? "header__link--active" : ""}`}
                  onClick={(e) => handleNavClick(e, link)}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="header__actions">
              {isLoggedIn ? (
                <div className="header__user-group">
                  {/* Desktop User Menu Dropdown Trigger */}
                  <div className="header__user-menu-wrapper" ref={userMenuRef}>
                    <button
                      type="button"
                      className={`header__profile-badge ${userDropdownOpen ? "header__profile-badge--open" : ""
                        }`}
                      title="Užívateľské menu"
                      onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    >
                      <div className="header__profile-avatar">
                        {userName ? (
                          <span>{userName.charAt(0).toUpperCase()}</span>
                        ) : userEmail ? (
                          <span>{userEmail.charAt(0).toUpperCase()}</span>
                        ) : (
                          <User size={14} />
                        )}
                      </div>
                      <div className="header__profile-details">
                        <div className="header__profile-name-row">
                          <span className="header__profile-name">
                            {userName || userEmail?.split("@")[0] || "Môj profil"}
                          </span>
                          {isAdmin && (
                            <span className="header__admin-pill">Admin</span>
                          )}
                          <ChevronDown
                            size={14}
                            className={`header__dropdown-arrow ${userDropdownOpen ? "header__dropdown-arrow--open" : ""
                              }`}
                          />
                        </div>
                        <div className="header__profile-credits-row">
                          <Sparkles size={11} className="header__credits-icon" />
                          <span className="header__credits-val">
                            {userCredits}{" "}
                            {userCredits === 1
                              ? "kredit"
                              : userCredits >= 2 && userCredits <= 4
                                ? "kredity"
                                : "kreditov"}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* Rich User Dropdown Menu */}
                    {userDropdownOpen && (
                      <div className="header__user-dropdown">
                        <div className="header__dropdown-user-info">
                          <div className="header__dropdown-avatar">
                            {userName ? (
                              <span>{userName.charAt(0).toUpperCase()}</span>
                            ) : userEmail ? (
                              <span>{userEmail.charAt(0).toUpperCase()}</span>
                            ) : (
                              <User size={16} />
                            )}
                          </div>
                          <div className="header__dropdown-user-meta">
                            <strong className="header__dropdown-user-name">
                              {userName || "Prihlásený používateľ"}
                            </strong>
                            <span className="header__dropdown-user-email">
                              {userEmail}
                            </span>
                          </div>
                        </div>

                        <div className="header__dropdown-credits-badge">
                          <Sparkles size={14} />
                          <span>Dostupné kredity:</span>
                          <strong>
                            {userCredits}{" "}
                            {userCredits === 1
                              ? "kredit"
                              : userCredits >= 2 && userCredits <= 4
                                ? "kredity"
                                : "kreditov"}
                          </strong>
                        </div>

                        <div className="header__dropdown-section">
                          <span className="header__dropdown-section-title">Môj účet</span>

                          <Link
                            to="/profil"
                            className="header__dropdown-link"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <User size={15} />
                            <span>Môj profil</span>
                          </Link>
                          <Link
                            to="/moje-objednavky"
                            className="header__dropdown-link"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <Package size={15} />
                            <span>Moje objednávky</span>
                          </Link>
                          <Link
                            to="/moj-rozvrh"
                            className="header__dropdown-link"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <CalendarDays size={15} />
                            <span>Rozvrh mojich detí</span>
                          </Link>
                          {(isLektor || isAdmin) && (
                            <Link
                              to="/lektor"
                              className="header__dropdown-link"
                              onClick={() => setUserDropdownOpen(false)}
                            >
                              <GraduationCap size={15} />
                              <span>Rozhranie lektora</span>
                            </Link>
                          )}
                        </div>

                        {isAdmin && (
                          <div className="header__dropdown-section header__dropdown-section--admin">
                            <span className="header__dropdown-section-title">
                              <Shield size={12} /> Administrácia
                            </span>
                            <div className="header__dropdown-admin-grid">
                              {ADMIN_NAV_LINKS.map((item) => {
                                const Icon = item.icon;
                                const targetTo = item.directPath || `/admin?tab=${item.key}`;
                                return (
                                  <Link
                                    key={item.key}
                                    to={targetTo}
                                    className="header__dropdown-link"
                                    onClick={() => setUserDropdownOpen(false)}
                                  >
                                    <Icon size={14} />
                                    <span>{item.label}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div className="header__dropdown-footer">
                          <button
                            type="button"
                            className="header__dropdown-logout-btn"
                            onClick={() => {
                              setUserDropdownOpen(false);
                              logout();
                              navigate("/");
                            }}
                          >
                            <LogOut size={15} />
                            <span>Odhlásiť sa</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="header__guest-group">
                  <Button
                    variant="primary"
                    size="sm"
                    className="header__btn-login"
                    onClick={() => {
                      setIsOpen(false);
                      openLoginModal();
                    }}
                  >
                    <LogIn size={16} />
                    <span>Prihlásiť sa</span>
                  </Button>
                </div>
              )}
            </div>
          </nav>

          <button
            className={`header__burger ${isOpen ? "header__burger--open" : ""}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>

          <div
            className={`header__overlay ${isOpen ? "header__overlay--visible" : ""}`}
            onClick={() => setIsOpen(false)}
          />
        </div>
      </header>

      {/* Login Modal */}
      <Modal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        title="Prihlásenie"
      >
        <LoginForm
          ref={loginCaptchaRef}
          onSubmit={(data) => {
            handleLogin(data);
          }}
          onSwitchToRegister={() => {
            setLoginModalOpen(false);
            setRegisterModalOpen(true);
          }}
          onForgotPassword={() => {
            setLoginModalOpen(false);
            setForgotModalOpen(true);
          }}
          errorMessage={errorMessageLogin}
          loggingIn={loggingIn}
          setLoggingIn={setLoggingIn}
          setCaptchaToken={setCaptchaTokenLogin}
        />
      </Modal>

      {/* Register Modal */}
      <Modal
        isOpen={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        title={registerSuccess ? "Registrácia úspešná" : "Registrácia"}
      >
        {registerSuccess ? (
          <div className="header__register-success">
            <div className="header__register-success-icon">
              <Check />
            </div>
            <p className="header__register-success-text">
              Registrácia prebehla úspešne! Na váš email sme odoslali overovací
              odkaz. Po overení sa môžete prihlásiť.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setRegisterModalOpen(false);
                setLoginModalOpen(true);
              }}
            >
              Prejsť na prihlásenie
            </Button>
          </div>
        ) : (
          <RegisterForm
            ref={registerCaptchaRef}
            onSubmit={(data) => {
              handleRegister(data);
            }}
            onSwitchToLogin={() => {
              setRegisterModalOpen(false);
              setLoginModalOpen(true);
            }}
            errorMessage={errorMessageRegistration}
            registrationinng={registrationinng}
            setRegistrationing={setRegistrationing}
            setCaptchaToken={setCaptchaTokenRegister}
          />
        )}
      </Modal>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={forgotModalOpen}
        onClose={() => setForgotModalOpen(false)}
        title={forgotSuccess ? "Email odoslaný" : "Zabudnuté heslo"}
      >
        {forgotSuccess ? (
          <div className="header__register-success">
            <div className="header__register-success-icon">
              <Check />
            </div>
            <p className="header__register-success-text">
              Nové heslo bolo odoslané na váš email. Skontrolujte svoju schránku
              a prihláste sa.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setForgotModalOpen(false);
                setLoginModalOpen(true);
              }}
            >
              Prejsť na prihlásenie
            </Button>
          </div>
        ) : (
          <ForgotPasswordForm
            ref={forgotCaptchaRef}
            onSubmit={(data) => {
              handleForgotPassword(data);
            }}
            onBackToLogin={() => {
              setForgotModalOpen(false);
              setLoginModalOpen(true);
            }}
            errorMessage={errorMessageForgot}
            forgotting={forgotting}
            setForgetting={setForgetting}
            setCaptchaToken={setCaptchaTokenForgot}
          />
        )}
      </Modal>

      {/* Resend Verification Modal */}
      <Modal
        isOpen={resendOpen}
        onClose={() => setResendOpen(false)}
        title="Overenie emailu"
      >
        <div className="header__resend-verify">
          {resendStatus === "success" ? (
            <>
              <div className="header__resend-verify-icon header__resend-verify-icon--success">
                <Check />
              </div>
              <p className="header__resend-verify-text">{resendMessage}</p>
              <Button
                variant="primary"
                onClick={() => {
                  setResendOpen(false);
                  setLoginModalOpen(true);
                }}
              >
                Prejsť na prihlásenie
              </Button>
            </>
          ) : (
            <>
              <div className="header__resend-verify-icon">
                <Mail />
              </div>
              <p className="header__resend-verify-text">
                Váš email ešte nebol overený. Kliknite na tlačidlo nižšie a
                pošleme vám nový overovací odkaz na{" "}
                <strong>{resendEmail}</strong>.
              </p>
              {resendStatus === "error" && (
                <p className="header__resend-verify-error">{resendMessage}</p>
              )}
              <Button
                variant="primary"
                disabled={resendStatus === "loading"}
                onClick={async () => {
                  setResendStatus("loading");
                  await axios
                    .post(RESEND_VERIFICATION_URL, { email: resendEmail })
                    .then(() => {
                      setResendStatus("success");
                      setResendMessage(
                        "Overovací email bol úspešne odoslaný! Skontrolujte svoju schránku.",
                      );
                    })
                    .catch((err) => {
                      setResendStatus("error");
                      const msg = handleApiError(err, "Nepodarilo sa odoslať overovací email. Skúste to znova.");
                      setResendMessage(msg);
                    });
                }}
              >
                {resendStatus === "loading"
                  ? "Odosiela sa..."
                  : "Odoslať overovací email"}
              </Button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

export default Header;
